import { localDayKey, type Streaks } from "@versedrill/core";

/**
 * Everything the streak card and the activity grid show, as plain functions of "the days this player has played".
 * Days are the player's own local calendar days ("2026-10-13"), built from calendar parts (never by adding 24 hours),
 * so daylight-saving changes can't shift or skip a day.
 */

const dayOf = (y: number, m: number, d: number) => localDayKey(new Date(y, m, d));
const parse = (key: string): Date => {
  const [y, m, d] = key.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
};
export const addDays = (key: string, n: number): string => {
  const d = parse(key);
  return dayOf(d.getFullYear(), d.getMonth(), d.getDate() + n);
};

/** History kept in the saved data: a little over a year, so the grid is always full and the file stays small. */
export const KEEP_DAYS = 400;

/** Add `key` to a sorted list of played days (no duplicates), dropping anything older than KEEP_DAYS. */
export function addPlayedDay(days: readonly string[], key: string): string[] {
  const oldest = addDays(key, -KEEP_DAYS);
  const set = new Set(days);
  set.add(key);
  return [...set].filter((d) => d >= oldest).sort();
}

/**
 * Players who were already playing have a current streak but no record of which days. A streak is, by definition,
 * consecutive days ending on the last day played, so those days are known exactly. Earlier history is not recoverable.
 */
export function seedPlayedDays(streaks: Streaks): string[] {
  if (streaks.currentStreak < 1 || streaks.currentStreakStart === 0) return [];
  const last = localDayKey(new Date(streaks.currentStreakStart));
  return Array.from({ length: Math.min(streaks.currentStreak, KEEP_DAYS) }, (_, i) => addDays(last, -(streaks.currentStreak - 1 - i)));
}

/**
 * The streak to show right now. The saved number only resets when the player next finishes a game, so it can still say
 * "7" the morning after a missed day. A streak is alive if the last day played was today or yesterday.
 */
export function effectiveStreak(streaks: Streaks, today: string): number {
  if (streaks.currentStreak < 1 || streaks.currentStreakStart === 0) return 0;
  const last = localDayKey(new Date(streaks.currentStreakStart));
  return last === today || last === addDays(today, -1) ? streaks.currentStreak : 0;
}

/**
 * Streaks worked out from the days played: the current streak is the run of consecutive days ending on the last day
 * played (alive only if that day is today or yesterday); the longest is the longest run, or `longestFloor` if a device
 * remembers a better one from before days were recorded.
 */
export function streaksFromDays(days: readonly string[], longestFloor: number, now: Date = new Date()): Streaks {
  const sorted = [...new Set(days)].sort();
  if (sorted.length === 0) return { currentStreak: 0, longestStreak: longestFloor, currentStreakStart: 0 };
  let longest = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    run = sorted[i] === addDays(sorted[i - 1]!, 1) ? run + 1 : 1;
    longest = Math.max(longest, run);
  }
  const last = sorted.at(-1)!;
  const today = localDayKey(now);
  const alive = last === today || last === addDays(today, -1);
  return { currentStreak: alive ? run : 0, longestStreak: Math.max(longest, longestFloor), currentStreakStart: alive ? parse(last).setHours(12, 0, 0, 0) : 0 };
}

export function streakHeadline(streak: number): { title: string; subtitle: string } {
  if (streak === 0) return { title: "No streak yet", subtitle: "Solve today’s verse to start one." };
  const title = `${streak}-day streak`;
  if (streak === 1) return { title, subtitle: "A good start. Come back tomorrow." };
  if (streak < 7) return { title, subtitle: "Build your daily habit." };
  if (streak < 30) return { title, subtitle: "A week and counting. Keep it going." };
  return { title, subtitle: "A month of showing up. Well done." };
}

export interface WeekDay {
  key: string;
  /** M T W T F S S */
  label: string;
  dayOfMonth: number;
  played: boolean;
  isToday: boolean;
  isFuture: boolean;
}

const LETTERS = ["M", "T", "W", "T", "F", "S", "S"];
/** 0 = Monday … 6 = Sunday. */
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;

/** The seven days of the player's current week, Monday first. */
export function weekDays(todayKey: string, played: ReadonlySet<string>): WeekDay[] {
  const monday = addDays(todayKey, -mondayIndex(parse(todayKey)));
  return LETTERS.map((label, i) => {
    const key = addDays(monday, i);
    return { key, label, dayOfMonth: parse(key).getDate(), played: played.has(key), isToday: key === todayKey, isFuture: key > todayKey };
  });
}

export interface WeekCell {
  /** First and last day of this week within this month (inclusive). */
  from: string;
  to: string;
  /** Days played in that span, 0–7. */
  count: number;
  /** Contains today. */
  isCurrent: boolean;
}
export interface MonthColumn {
  key: string; // "2026-10"
  label: string; // "Oct"
  name: string; // "October"
  weeks: WeekCell[];
}

/**
 * The past year as 12 month columns ending with this month. Each column is that month cut into calendar weeks
 * (Monday to Sunday, clipped to the month); a cell's shade is how many days of that week were played.
 * Weeks that haven't started yet are left out.
 */
export function activityGrid(todayKey: string, played: ReadonlySet<string>, locale?: string): { months: MonthColumn[]; total: number } {
  const today = parse(todayKey);
  const months: MonthColumn[] = [];
  let total = 0;
  for (let back = 11; back >= 0; back--) {
    const first = new Date(today.getFullYear(), today.getMonth() - back, 1);
    const y = first.getFullYear();
    const m = first.getMonth();
    const lastDay = new Date(y, m + 1, 0).getDate();
    const weeks: WeekCell[] = [];
    let d = 1;
    while (d <= lastDay) {
      const start = new Date(y, m, d);
      const span = Math.min(7 - mondayIndex(start), lastDay - d + 1); // days left in this Monday-to-Sunday week
      const from = dayOf(y, m, d);
      const to = dayOf(y, m, d + span - 1);
      if (from > todayKey) break;
      let count = 0;
      for (let i = 0; i < span; i++) if (played.has(dayOf(y, m, d + i))) count++;
      total += count;
      weeks.push({ from, to, count, isCurrent: from <= todayKey && todayKey <= to });
      d += span;
    }
    months.push({
      key: `${y}-${String(m + 1).padStart(2, "0")}`,
      label: new Intl.DateTimeFormat(locale, { month: "short" }).format(first),
      name: new Intl.DateTimeFormat(locale, { month: "long" }).format(first),
      weeks,
    });
  }
  return { months, total };
}
